import { useState, useMemo, useEffect } from 'react';

import { useMutation } from '@apollo/client';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import SimpleSelect from 'components/selects/SimpleSelect';
import { UPDATE_ORDER_STATUS } from 'gql/orders';
import STATUS_COLOR_MAPPING from 'helpers/statusColorMapping';
import { ORDER_STATUS_LABEL_MAPPING, ORDER_STATUSES } from 'lib/orderStatuses';

function OrderStatus({ id, value, options, invoiceURL, reasonCanceled }) {
  const [showSelectStatus, setShowSelectStatus] = useState(false);
  const [statusSelected, setStatusSelected] = useState('');
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const [updateStatus, { loading, error, reset }] = useMutation(
    UPDATE_ORDER_STATUS,
    {
      onError: () => setShowSelectStatus(false),
      onCompleted: () => setShowSelectStatus(false),
    },
  );
  const onClickEditStatus = () => {
    reset();
    setStatusSelected('');
    setCancelReason('');
    setShowSelectStatus(true);
  };
  const onClickCancelUpdateStatus = () => {
    setShowSelectStatus(false);
  };
  const selectOptions = useMemo(
    () =>
      options.map(({ value: statusValue }) => ({
        value: statusValue,
        label: (
          <Typography variant="caption">
            {ORDER_STATUS_LABEL_MAPPING[statusValue]}
          </Typography>
        ),
      })),
    [value, options],
  );

  const handleUpdateStatus = () => {
    updateStatus({
      variables: {
        id,
        status: statusSelected,
        reasonCanceled:
          statusSelected === ORDER_STATUSES.canceled
            ? cancelReason.trim() || null
            : null,
      },
    });
  };

  useEffect(() => {
    if (statusSelected === ORDER_STATUSES.canceled) {
      setShowConfirmCancel(true);
    }
  }, [statusSelected]);

  const onSaveStatus = () => {
    handleUpdateStatus();
  };

  const isDisabledEditButton = useMemo(
    () => selectOptions.length === 0,
    [selectOptions],
  );
  return (
    <Box
      key={`update-order-status${id}-box`}
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {!showSelectStatus && (
          <Tooltip
            title={
              value === ORDER_STATUSES.canceled && reasonCanceled
                ? `Motivo: ${reasonCanceled}`
                : 'La orden tiene esta secuencia de estados: pendiente, procesando, lista en tienda, delivery confirmado, entregado a transportista, en camino y entregada.'
            }
          >
            <Chip
              label={
                <Typography variant="caption" sx={{ color: 'inherit' }}>
                  {ORDER_STATUS_LABEL_MAPPING[value]}
                </Typography>
              }
              sx={{ borderWidth: '2px' }}
              color={STATUS_COLOR_MAPPING[value]}
              variant="outlined"
            />
          </Tooltip>
        )}
        {showSelectStatus && (
          <SimpleSelect
            id={`update-order-status${id}`}
            variant="outlined"
            options={selectOptions}
            label="Estado"
            controlled={{
              controlledValue: statusSelected,
              setControlledValue: setStatusSelected,
            }}
            disabled={loading}
            InputProps={{
              sx: {
                width: 150,
                height: '32px',
                borderRadius: '20px',
                borderWidth: '2px',
              },
            }}
          />
        )}
        {!showSelectStatus && (
          <Tooltip
            title={
              isDisabledEditButton
                ? 'No es posible cambiar de estado'
                : 'Editar estado de la orden'
            }
          >
            <span>
              <IconButton
                id="invoiceButton"
                variant="body2"
                onClick={onClickEditStatus}
                target="_blank"
                rel="noopener noreferrer"
                disabled={isDisabledEditButton}
              >
                <EditOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {showSelectStatus && (
          <Tooltip title="Cancelar acción">
            <span>
              <IconButton
                id="invoiceButton"
                variant="body2"
                onClick={onClickCancelUpdateStatus}
                disabled={loading}
                target="_blank"
                rel="noopener noreferrer"
              >
                <UndoOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {showSelectStatus && (
          <Tooltip title="Actualizar estado">
            <span>
              {!loading && (
                <IconButton
                  id="invoiceButton"
                  variant="body2"
                  onClick={onSaveStatus}
                  disabled={statusSelected === ''}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={(theme) => ({ color: theme.palette.icons.valid })}
                >
                  <SaveOutlinedIcon />
                </IconButton>
              )}
            </span>
          </Tooltip>
        )}
        {loading && <CircularProgress size={15} color="secondary" />}
      </Box>
      {error && (
        <Typography
          color="error"
          variant="caption"
          noWrap
          key={`update-order-status${id}-error`}
        >
          Error para actualizar el estado
        </Typography>
      )}
      <Dialog
        open={showConfirmCancel}
        onClose={() => {
          setStatusSelected('');
          setCancelReason('');
          setShowConfirmCancel(false);
        }}
      >
        <DialogTitle>Confirmar cancelación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {invoiceURL
              ? 'Esta orden ya tiene asociado un comprobante electrónico (boleta/factura). ¿Estás seguro que deseas cancelarla?'
              : '¿Estás seguro que deseas cancelar esta orden?'}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            multiline
            label="Motivo de cancelación (Obligatorio)"
            type="text"
            fullWidth
            variant="outlined"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ mt: 2 }}
            error={!cancelReason.trim()}
            helperText={
              !cancelReason.trim()
                ? 'Debes ingresar un motivo para continuar'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStatusSelected('');
              setCancelReason('');
              setShowConfirmCancel(false);
            }}
          >
            No
          </Button>
          <Button
            onClick={() => setShowConfirmCancel(false)}
            color="error"
            disabled={!cancelReason.trim()}
          >
            Si
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

OrderStatus.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
    }),
  ).isRequired,
  invoiceURL: PropTypes.string,
  reasonCanceled: PropTypes.string,
};

OrderStatus.defaultProps = {
  invoiceURL: undefined,
  reasonCanceled: undefined,
};

export default OrderStatus;
